import { CheckCircle2, Circle, FileText, ReceiptText, ScrollText, FileQuestion, Calendar } from "lucide-react";
import { fileUrl } from "@/lib/api";

const STAGES = ["Discovery", "Booking", "Design", "Execution", "Handover"];

// Wrap /api/files/{id} URLs with an auth query so <img>/<a> can load them
const resolveUrl = (u) => (u && u.startsWith("/api/files/") ? fileUrl(u) : u);

export function ProgressRing({ percent = 0, size = 132 }) {
  const r = (size - 16) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (Math.min(100, Math.max(0, percent)) / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="progress-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EDE5D8" strokeWidth="10" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#29231F" strokeWidth="10"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
          style={{ transition: "stroke-dashoffset 1s cubic-bezier(0.22,1,0.36,1)" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <span className="font-display text-4xl text-espresso leading-none">{percent}%</span>
          <p className="fine-uppercase text-[0.58rem] text-champagne mt-1">Complete</p>
        </div>
      </div>
    </div>
  );
}

export function StageStepper({ current }) {
  const idx = STAGES.indexOf(current);
  return (
    <div className="flex items-center w-full" data-testid="stage-stepper">
      {STAGES.map((s, i) => {
        const done = i <= idx;
        return (
          <div key={s} className="flex-1 flex flex-col items-center relative">
            {i > 0 && <span className={`absolute top-3 right-1/2 w-full h-0.5 ${i <= idx ? "bg-olive" : "bg-olive/20"}`} />}
            <span className="relative z-10 bg-cream rounded-full">
              {done ? <CheckCircle2 size={26} className="text-olive" /> : <Circle size={26} className="text-olive/25" />}
            </span>
            <span className={`mt-2 text-[0.62rem] uppercase tracking-[0.12em] text-center ${done ? "text-olive font-bold" : "text-ink/40"}`}>{s}</span>
          </div>
        );
      })}
    </div>
  );
}

const DOC_ICON = { Invoice: ReceiptText, Contract: ScrollText, Receipt: FileText, Other: FileQuestion };

export default function ProjectView({ project }) {
  const docsByType = (type) => (project.documents || []).filter((d) => d.type === type);
  return (
    <div data-testid={`project-${project.id}`} className="space-y-10">
      {/* Overview */}
      <div className="bg-cream border border-olive/15 rounded-xl p-8 md:p-10">
        <div className="flex flex-col md:flex-row md:items-center gap-8">
          <ProgressRing percent={project.completion_percent || 0} />
          <div className="flex-1">
            <span className="overline text-bronze">{project.status}</span>
            <h2 className="font-display text-4xl text-olive mt-1">{project.title}</h2>
            {project.address && <p className="text-ink/60 mt-1">{project.address}</p>}
            <div className="flex flex-wrap gap-6 mt-4 text-sm text-ink/70">
              {project.start_date && <span className="flex items-center gap-2"><Calendar size={15} className="text-bronze" /> Started {project.start_date}</span>}
              {project.expected_handover && <span className="flex items-center gap-2"><Calendar size={15} className="text-bronze" /> Handover {project.expected_handover}</span>}
            </div>
          </div>
        </div>
        <div className="mt-10 pt-8 border-t border-olive/15">
          <StageStepper current={project.stage} />
        </div>
      </div>

      {/* Daily updates */}
      <div>
        <h3 className="font-display text-3xl text-olive mb-5">Daily Progress</h3>
        {(!project.updates || project.updates.length === 0) ? (
          <p className="text-ink/50 bg-cream/60 rounded-md p-6" data-testid="no-updates">No updates yet. Your team will post daily site progress here.</p>
        ) : (
          <div className="space-y-6" data-testid="updates-feed">
            {project.updates.map((u) => (
              <div key={u.id} className="bg-white border border-olive/12 rounded-lg p-6 soft-shadow">
                <div className="flex items-baseline justify-between gap-4">
                  <h4 className="font-display text-2xl text-olive">{u.title}</h4>
                  <span className="text-sm text-bronze whitespace-nowrap">{u.date}</span>
                </div>
                {u.note && <p className="text-ink/70 text-lg leading-relaxed mt-2">{u.note}</p>}
                {u.photos && u.photos.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
                    {u.photos.map((p, i) => {
                      const src = resolveUrl(p);
                      return (
                        <a key={i} href={src} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-md aspect-square">
                          <img src={src} alt="Site progress" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documents */}
      <div>
        <h3 className="font-display text-3xl text-olive mb-5">Documents</h3>
        <div className="grid md:grid-cols-3 gap-6" data-testid="documents-section">
          {["Invoice", "Contract", "Receipt"].map((type) => {
            const Icon = DOC_ICON[type];
            const items = docsByType(type).concat(type === "Receipt" ? docsByType("Other") : []);
            return (
              <div key={type} className="bg-cream border border-olive/15 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon size={20} className="text-bronze" />
                  <span className="overline text-bronze">{type === "Receipt" ? "Receipts & Others" : type + "s"}</span>
                </div>
                {items.length === 0 ? (
                  <p className="text-ink/45 text-sm">None yet.</p>
                ) : (
                  <ul className="space-y-3">
                    {items.map((d) => (
                      <li key={d.id} className="flex items-center justify-between gap-3 border-b border-olive/10 pb-3 last:border-0">
                        <div>
                          <a href={resolveUrl(d.url)} target="_blank" rel="noreferrer" className="text-olive font-medium link-underline">{d.title}</a>
                          <p className="text-xs text-ink/50 mt-0.5">
                            {d.date}{d.amount ? ` · ₹${d.amount}` : ""}{d.status ? ` · ${d.status}` : ""}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
